/**
 * Tracks whether the current tab has performed any in-app (client-side)
 * navigation since it loaded, as opposed to landing directly on the current
 * URL (e.g. opening a shared link from WhatsApp/notifications).
 *
 * `window.history.length` is NOT a reliable signal for this on its own —
 * it also counts entries created before the app ever loaded (e.g. the
 * browser's own history from previous unrelated pages), so a user who
 * opened a shared link can still have `history.length > 1`. Instead we use
 * a `sessionStorage` counter that only increments when the SPA's router
 * itself changes location, so it accurately reflects "has this tab
 * navigated inside CourtVibe" regardless of prior browser history.
 *
 * `sessionStorage` is scoped per-tab and cleared when the tab/window is
 * closed, so a fresh tab opened from a shared link always starts at 0 —
 * exactly the case we need to detect.
 */

const STORAGE_KEY = "cv_internal_nav_count";

export function markInternalNavigation(): void {
  try {
    const current = Number(sessionStorage.getItem(STORAGE_KEY) ?? "0");
    sessionStorage.setItem(STORAGE_KEY, String(current + 1));
  } catch {
    // sessionStorage unavailable (e.g. private browsing with storage
    // disabled) — silently ignore. Callers should treat "no internal
    // history" as the safe default when this happens.
  }
}

export function hasInternalHistory(): boolean {
  try {
    return Number(sessionStorage.getItem(STORAGE_KEY) ?? "0") > 0;
  } catch {
    return false;
  }
}
